// src/stores/useAnnotationStore.js
//
// Manages the in-progress annotation session.
// An "annotation layer" is a named collection of GeoJSON features
// drawn by the user. On save, it's handed off to useUserLayersStore
// (same path as an uploaded layer).
//
// Tool modes:
//   "pin"     → Point
//   "line"    → LineString, any number of vertices; double-click ends it
//   "polygon" → Polygon   (multi-click, double-click to close)
//   "note"    → Point with a text label property
//   null      → no active drawing tool
//
// ── LABEL INVARIANT ──────────────────────────────────────────────────
// Every annotation carries its label in TWO places that must not drift:
//   ann.label                     — what the panel list renders
//   ann.geojson.properties.label  — what is actually saved and drawn
// Only the second survives toGeoJSON(). Line and polygon used to omit it
// entirely, so their labels existed only in the panel and were dropped on
// save — invisible until the layer came back with unnamed features. Both
// are now written through makeAnnotation() and renameAnnotation() so there
// is one place each can be set. Anything that edits a label must go
// through renameAnnotation.
//
// `named` distinguishes a user-chosen label from an auto-assigned one, so
// the map can label the first without cluttering itself with "Pin 1,
// Pin 2, Pin 3" (see useAnnotationLayer's label layer filter).

import { create } from "zustand";

let _nextId = 1;
function nextId() { return `ann-${_nextId++}`; }

// Per-type counters. Monotonic on purpose: the old scheme derived the
// number from annotations.filter(type).length + 1, so deleting "Pin 1" and
// drawing another produced a second "Pin 1". Harmless while labels were
// decorative, confusing the moment they are user-facing and renameable.
const DEFAULT_LABEL = { pin: "Point", line: "Line", polygon: "Poly", note: "Note" };
let _counters = { pin: 0, line: 0, polygon: 0, note: 0 };
function nextLabel(type) {
  _counters[type] = (_counters[type] ?? 0) + 1;
  return `${DEFAULT_LABEL[type] ?? "Feature"} ${_counters[type]}`;
}
function resetCounters() { _counters = { pin: 0, line: 0, polygon: 0, note: 0 }; }

// Push the counters past whatever a loaded layer already uses, so reopening a
// layer that contains "Pin 3" and drawing a new pin yields "Pin 4" rather than
// a second "Pin 1". Only auto-shaped labels ("Pin 12") are considered; a
// renamed feature has no number to collide with.
function seedCountersFrom(annotations) {
  resetCounters();
  for (const a of annotations) {
    const prefix = DEFAULT_LABEL[a.type];
    if (!prefix) continue;
    const m = new RegExp(`^${prefix} (\\d+)$`).exec(a.label ?? "");
    if (m) _counters[a.type] = Math.max(_counters[a.type], Number(m[1]));
  }
}

// Geometry → annotation type, for features that predate this tool or came
// from an import. Layers drawn here carry `annotationType` and use it
// directly; this is only the fallback.
const TYPE_FROM_GEOMETRY = {
  Point: "pin",
  MultiPoint: "pin",
  LineString: "line",
  MultiLineString: "line",
  Polygon: "polygon",
  MultiPolygon: "polygon",
};

// Rebuild session annotations from a saved layer's FeatureCollection.
//
// Round-tripping is lossless for layers drawn here, because makeAnnotation
// writes annotationType, label, color and named into properties. Anything
// missing is inferred rather than dropped: type from the geometry, colour
// from the layer's own style, label from the per-type counter.
function annotationsFromGeoJSON(geojson, fallbackColor) {
  const features = geojson?.features ?? [];
  const out = [];
  resetCounters();
  for (const f of features) {
    if (!f?.geometry) continue;
    const p = f.properties ?? {};
    const type = p.annotationType ?? TYPE_FROM_GEOMETRY[f.geometry.type];
    if (!type) continue;                       // geometry we cannot represent
    const color = p.color ?? fallbackColor ?? "#16f2d4";
    const label = p.label ?? nextLabel(type);
    // named defaults true for a loaded label: it was good enough to save, so
    // it should keep rendering on the map rather than silently going quiet.
    const named = p.named ?? (p.label ? true : false);
    out.push({
      id: nextId(),
      type,
      color,
      label,
      named,
      geojson: {
        type: "Feature",
        properties: { ...p, label, color, annotationType: type, named },
        geometry: f.geometry,
      },
    });
  }
  seedCountersFrom(out);
  return out;
}

// Single constructor for every annotation, so the label invariant above
// cannot be violated by adding a branch that forgets `properties.label`.
function makeAnnotation({ type, color, geometry, label, named = false }) {
  const finalLabel = label ?? nextLabel(type);
  return {
    id: nextId(),
    type,
    color,
    label: finalLabel,
    named,
    geojson: {
      type: "Feature",
      properties: { label: finalLabel, color, annotationType: type, named },
      geometry,
    },
  };
}

export const useAnnotationStore = create((set, get) => ({

  // Whether the annotation panel is open
  open: false,

  // Active drawing tool
  tool: null,          // "pin" | "line" | "polygon" | "note" | null

  // Active stroke/fill color (hex)
  color: "#16f2d4",

  // In-progress polygon/line points [[lng, lat], ...]
  pendingPoints: [],

  // Committed annotations for the current session
  // Each item: { id, type, color, label, named, geojson: GeoJSON Feature }
  annotations: [],

  // Name the user gives the layer before saving
  layerName: "",

  // ── SELECTION AND EDIT ──────────────────────────────────────────
  // `selectedId` is the lighter state: the row is highlighted and the
  // feature carries a halo on the map. Set by clicking a row or the
  // feature itself.
  //
  // `editingId` is the annotation the user has actively opened for editing
  // via the row's pencil. It governs BOTH halves of editing at once — the
  // name is editable in place, and the geometry is live: a polygon gets
  // MlDraw's vertex handles, a pin or note becomes draggable. Exactly one
  // annotation can be in this state, and editing implies selection.
  selectedId: null,
  editingId: null,

  // The saved layer this session is editing, or null when drawing a new one.
  // Save branches on it: PATCH the existing row, or POST a new one.
  editingLayerId: null,

  // ── UI ──────────────────────────────────────────────────────────
  openPanel: () => {
    resetCounters();
    set({ open: true, tool: null, pendingPoints: [], annotations: [], layerName: "",
          selectedId: null, editingId: null, editingLayerId: null });
  },

  // Reopen a saved layer for editing. A separate entry point rather than an
  // argument to openPanel, because openPanel's job is to WIPE the session —
  // conflating the two would mean one function that either clears everything
  // or restores everything depending on a flag.
  openPanelForLayer: (layer) => {
    const annotations = annotationsFromGeoJSON(layer?.geojson, layer?.style?.color);
    set({
      open: true,
      tool: null,
      pendingPoints: [],
      annotations,
      layerName: layer?.name ?? "",
      selectedId: null,
      editingId: null,
      editingLayerId: layer?.id ?? null,
      color: layer?.style?.color ?? "#16f2d4",
    });
  },

  closePanel: () => set({ open: false, tool: null, pendingPoints: [],
                          selectedId: null, editingId: null, editingLayerId: null }),

  // ── TOOL ────────────────────────────────────────────────────────
  // Picking a draw tool drops the edit selection: the next map click is a
  // new feature, not a change to the selected one.
  setTool: (tool) => set({ tool, pendingPoints: [], selectedId: null, editingId: null }),
  setColor: (color) => set({ color }),
  setLayerName: (layerName) => set({ layerName }),

  // ── SELECTION ───────────────────────────────────────────────────
  selectAnnotation: (id) => set(s => ({
    selectedId: id,
    // Selecting a different annotation ends any geometry edit in progress.
    editingId: s.editingId === id ? s.editingId : null,
    // Selecting is an edit gesture, so it cancels drawing.
    tool: id ? null : s.tool,
    pendingPoints: id ? [] : s.pendingPoints,
  })),

  setEditingId: (id) => set({ editingId: id }),

  // ── POINT CAPTURE ───────────────────────────────────────────────
  // Called by the map click handler with { lng, lat }
  handleMapClick: (lngLat, labelOverride) => {
    const { tool, color, pendingPoints } = get();

    if (tool === "pin") {
      set(s => ({
        annotations: [...s.annotations, makeAnnotation({
          type: "pin",
          color,
          label: labelOverride,
          named: !!labelOverride,
          geometry: { type: "Point", coordinates: [lngLat.lng, lngLat.lat] },
        })],
      }));
      return;
    }

    if (tool === "note") {
      const text = labelOverride ?? "";
      if (!text) return; // caller must supply label for notes
      set(s => ({
        annotations: [...s.annotations, makeAnnotation({
          type: "note",
          color,
          label: text,
          named: true,
          geometry: { type: "Point", coordinates: [lngLat.lng, lngLat.lat] },
        })],
      }));
      return;
    }

    // A LineString of ANY length, not a two-click segment. Every click extends
    // it; a double-click ends it, the same gesture that closes a polygon two
    // branches down. Committing at exactly two points made "line" mean
    // "segment", and drawing a path meant stacking separate annotations that
    // could not be edited, coloured or deleted as one thing.
    if (tool === "line") {
      set({ pendingPoints: [...pendingPoints, [lngLat.lng, lngLat.lat]] });
      return;
    }

    if (tool === "polygon") {
      set({ pendingPoints: [...pendingPoints, [lngLat.lng, lngLat.lat]] });
    }
  },

  /**
   * Commit the in-progress LineString. Double-click.
   *
   * DEDUPES THE LAST VERTEX. MapLibre fires the second tap's `click` before
   * `dblclick`, so by the time this runs a duplicate point has already been
   * appended at the same position — the same behaviour useMeasure documents and
   * strips. Without this every line ends with a zero-length final segment,
   * which is invisible on the map and wrong in the geometry.
   *
   * Compared in degrees rather than metres: the two taps of a double-click land
   * within a pixel or two, and at z18 a pixel is 0.25 m, so 1e-7 degrees
   * (~1 cm) separates "the same click twice" from "two deliberate vertices"
   * without needing a haversine here.
   */
  closePendingLine: () => {
    const { pendingPoints, color } = get();
    let pts = pendingPoints;
    if (pts.length >= 2) {
      const [ax, ay] = pts[pts.length - 2];
      const [bx, by] = pts[pts.length - 1];
      if (Math.abs(ax - bx) < 1e-7 && Math.abs(ay - by) < 1e-7) pts = pts.slice(0, -1);
    }
    // Two points is the shortest real line. One is a click that never became
    // anything, and committing it would write a LineString of length zero.
    if (pts.length < 2) return;
    set(s => ({
      pendingPoints: [],
      annotations: [...s.annotations, makeAnnotation({
        type: "line",
        color,
        geometry: { type: "LineString", coordinates: pts },
      })],
    }));
  },

  // Close and commit the in-progress polygon
  closePendingPolygon: () => {
    const { pendingPoints, color } = get();
    // Same duplicate-vertex strip as closePendingLine: the second tap's click
    // has already appended a point. Previously this left every polygon with a
    // repeated vertex before the closing one — valid geometry, and a stray
    // handle sitting on top of another the moment anyone vertex-edited it.
    let pts = pendingPoints;
    if (pts.length >= 2) {
      const [ax, ay] = pts[pts.length - 2];
      const [bx, by] = pts[pts.length - 1];
      if (Math.abs(ax - bx) < 1e-7 && Math.abs(ay - by) < 1e-7) pts = pts.slice(0, -1);
    }
    if (pts.length < 3) return;
    const ring = [...pts, pts[0]]; // close ring
    set(s => ({
      pendingPoints: [],
      annotations: [...s.annotations, makeAnnotation({
        type: "polygon",
        color,
        geometry: { type: "Polygon", coordinates: [ring] },
      })],
    }));
  },

  cancelPending: () => set({ pendingPoints: [] }),

  // ── EDIT ────────────────────────────────────────────────────────
  // The only sanctioned way to change a label. Writes both copies.
  renameAnnotation: (id, label) => {
    const trimmed = String(label ?? "").trim();
    if (!trimmed) return;                       // empty name keeps the old one
    set(s => ({
      annotations: s.annotations.map(a =>
        a.id !== id ? a : {
          ...a,
          label: trimmed,
          named: true,
          geojson: {
            ...a.geojson,
            properties: { ...a.geojson.properties, label: trimmed, named: true },
          },
        }
      ),
    }));
  },

  // Recolor one annotation. Same dual-write shape as renameAnnotation:
  // `color` is read by the panel row, `properties.color` by every paint
  // expression in useAnnotationLayer.
  recolorAnnotation: (id, color) => set(s => ({
    annotations: s.annotations.map(a =>
      a.id !== id ? a : {
        ...a,
        color,
        geojson: { ...a.geojson, properties: { ...a.geojson.properties, color } },
      }
    ),
  })),

  // Replace one annotation's geometry — used by the pin/note drag handler
  // and by the MlDraw vertex-edit write-back. Geometry only; label, color
  // and id are preserved.
  updateAnnotationGeometry: (id, geometry) => set(s => ({
    annotations: s.annotations.map(a =>
      a.id !== id ? a : { ...a, geojson: { ...a.geojson, geometry } }
    ),
  })),

  deleteAnnotation: (id) => set(s => ({
    annotations: s.annotations.filter(a => a.id !== id),
    selectedId: s.selectedId === id ? null : s.selectedId,
    editingId: s.editingId === id ? null : s.editingId,
  })),

  clearAll: () => {
    resetCounters();
    set({ annotations: [], pendingPoints: [], selectedId: null, editingId: null });
  },

  // ── SERIALISE ───────────────────────────────────────────────────
  // Returns a GeoJSON FeatureCollection for the full session
  toGeoJSON: () => {
    const { annotations } = get();
    return {
      type: "FeatureCollection",
      features: annotations.map(a => a.geojson),
    };
  },
}));
